import { useEffect, useMemo, useRef, useState } from "react";
import AdminLayout from "../AdminLayout";
import { API_BASE } from "@/config/api";
import { uploadMedia } from "../uploadMedia";
import { normalizeMediaUrl } from "@/lib/mediaUrl";
import { SITE_TEXTS, type SiteTextDef } from "@/lib/siteTexts";

const defs: readonly SiteTextDef[] = SITE_TEXTS;
const inputCls = "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm";
const labelCls = "block text-xs font-medium text-foreground-700 mb-1";

function initialValues(overrides: Record<string, string> | undefined): Record<string, string> {
  const values: Record<string, string> = {};
  for (const d of defs) {
    values[d.key] = overrides && typeof overrides[d.key] === "string" ? overrides[d.key] : d.default;
  }
  return values;
}

export default function AdminSiteTexts() {
  const [values, setValues] = useState<Record<string, string>>(() => initialValues(undefined));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const uploadTarget = useRef<string | null>(null);

  useEffect(() => {
    fetch(`${API_BASE}/bootstrap`)
      .then((res) => res.json())
      .then((json) => setValues(initialValues(json.contentBlocks?.textos?.site?.[0]?.values)))
      .finally(() => setLoading(false));
  }, []);

  const groups = useMemo(() => {
    const map = new Map<string, SiteTextDef[]>();
    for (const d of defs) {
      if (!map.has(d.group)) map.set(d.group, []);
      map.get(d.group)!.push(d);
    }
    return [...map.entries()];
  }, []);

  const query = search.trim().toLocaleLowerCase("es");
  const matches = (d: SiteTextDef) =>
    !query ||
    d.label.toLocaleLowerCase("es").includes(query) ||
    d.group.toLocaleLowerCase("es").includes(query) ||
    (values[d.key] ?? "").toLocaleLowerCase("es").includes(query) ||
    d.default.toLocaleLowerCase("es").includes(query);

  const changedCount = defs.filter((d) => values[d.key] !== d.default).length;

  function set(key: string, value: string) {
    setValues((v) => ({ ...v, [key]: value }));
  }

  async function handleFile(file: File) {
    const key = uploadTarget.current;
    if (!key) return;
    setUploadingKey(key);
    setMessage(null);
    try {
      const { url } = await uploadMedia(file, defs.find((d) => d.key === key)?.label ?? "Imagen del sitio");
      set(key, url);
    } catch (err) {
      setMessage({ type: "error", text: err instanceof Error ? err.message : "No se pudo subir la imagen" });
    } finally {
      setUploadingKey(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleSave() {
    setMessage(null);
    const empty = defs.filter((d) => !d.optional && !(values[d.key] ?? "").trim());
    if (empty.length > 0) {
      const names = empty.slice(0, 5).map((d) => `«${d.label}» (${d.group})`);
      setMessage({
        type: "error",
        text: `Estos textos no pueden quedar vacíos: ${names.join(", ")}${empty.length > 5 ? " y otros" : ""}.`,
      });
      return;
    }

    const changed: Record<string, string> = {};
    for (const d of defs) {
      if (values[d.key] !== d.default) changed[d.key] = values[d.key];
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/content-blocks/textos/site`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [{ id: "config", values: changed }] }),
      });
      if (!res.ok) {
        setMessage({ type: "error", text: "No se pudo guardar" });
        return;
      }
      setMessage({
        type: "success",
        text: `Textos guardados (${Object.keys(changed).length} modificados). Los cambios ya se ven en el sitio.`,
      });
    } catch {
      setMessage({ type: "error", text: "Error de conexión." });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <AdminLayout>
        <p className="text-sm text-foreground-500">Cargando...</p>
      </AdminLayout>
    );
  }

  function renderField(d: SiteTextDef) {
    const value = values[d.key] ?? "";
    const changed = value !== d.default;
    return (
      <div key={d.key} data-text-key={d.key}>
        <label className={labelCls}>
          {d.label}
          {d.optional && <span className="font-normal text-foreground-400"> — déjalo vacío para ocultarlo</span>}
        </label>

        {d.kind === "image" ? (
          <div className="space-y-2">
            {value ? (
              <img src={normalizeMediaUrl(value)} alt="" className="w-full h-32 object-cover rounded-md" />
            ) : (
              <div className="w-full h-16 rounded-md bg-background-200 flex items-center justify-center text-xs text-foreground-500">
                Sin imagen: no se muestra
              </div>
            )}
            <div className="flex gap-2">
              <input
                className={inputCls}
                placeholder="URL de la imagen"
                value={value}
                onChange={(e) => set(d.key, e.target.value)}
              />
              <button
                type="button"
                disabled={uploadingKey === d.key}
                onClick={() => {
                  uploadTarget.current = d.key;
                  fileRef.current?.click();
                }}
                className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 disabled:opacity-60 cursor-pointer"
              >
                {uploadingKey === d.key ? "Subiendo..." : "Subir foto"}
              </button>
            </div>
          </div>
        ) : d.kind === "textarea" ? (
          <textarea
            className={inputCls}
            rows={d.default.includes("\n") ? 5 : 3}
            value={value}
            onChange={(e) => set(d.key, e.target.value)}
          />
        ) : (
          <input className={inputCls} value={value} onChange={(e) => set(d.key, e.target.value)} />
        )}

        {d.help && <p className="mt-1 text-xs text-foreground-500">{d.help}</p>}
        {changed && (
          <p className="mt-1 text-xs text-foreground-500">
            <button
              type="button"
              onClick={() => set(d.key, d.default)}
              className="font-medium text-primary-600 hover:text-primary-700 cursor-pointer"
            >
              Restaurar original
            </button>
            {d.kind !== "image" && <span className="text-foreground-400"> · Original: {d.default.replace(/\n/g, " / ")}</span>}
          </p>
        )}
      </div>
    );
  }

  return (
    <AdminLayout>
      <h1 className="font-heading font-bold text-2xl text-foreground-950">Textos del sitio</h1>
      <p className="mt-1 text-sm text-foreground-500">
        Encabezados, introducciones, botones, menú, pie de página, mensajes de WhatsApp y algunas fotos de las páginas
        públicas, agrupados por página. Los textos de cada bodega, plan, pregunta frecuente o testimonio se editan en
        su propio módulo, y el banner principal, «El recinto» y el formulario de contacto tienen el suyo. Lo que no
        cambies se queda con el texto original.
      </p>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
        className="hidden"
        onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
      />

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <input
          type="search"
          className={`${inputCls} max-w-sm`}
          placeholder="Buscar un texto (por ejemplo: WhatsApp, catálogo, seguridad)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="text-xs text-foreground-500">
          {changedCount === 0 ? "Sin cambios respecto de los originales" : `${changedCount} texto(s) modificado(s)`}
        </span>
      </div>

      {message && (
        <div
          className={`mt-4 rounded-md p-3 text-sm border ${
            message.type === "success"
              ? "bg-primary-100 border-primary-200 text-primary-800"
              : "bg-accent-100 border-accent-200 text-accent-800"
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="mt-5 space-y-3">
        {groups.map(([group, items]) => {
          const visible = items.filter(matches);
          if (visible.length === 0) return null;
          const modified = items.filter((d) => values[d.key] !== d.default).length;
          return (
            <details
              key={group}
              open={query ? true : undefined}
              className="rounded-lg border border-background-200 bg-background-50 group"
            >
              <summary className="flex cursor-pointer items-center justify-between gap-3 px-5 py-3.5 font-heading font-semibold text-foreground-900 list-none">
                <span>{group}</span>
                <span className="text-xs font-normal text-foreground-500">
                  {visible.length} texto(s){modified > 0 ? ` · ${modified} modificado(s)` : ""}
                </span>
              </summary>
              <div className="space-y-4 border-t border-background-200 px-5 py-4">{visible.map(renderField)}</div>
            </details>
          );
        })}
        {groups.every(([, items]) => items.filter(matches).length === 0) && (
          <p className="text-sm text-foreground-500">No hay textos que coincidan con la búsqueda.</p>
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60 cursor-pointer"
        >
          {saving ? "Guardando..." : "Guardar textos"}
        </button>
        <button
          type="button"
          onClick={() => {
            if (window.confirm("Se cargan todos los textos y fotos originales. Para aplicarlos tienes que presionar «Guardar textos». ¿Continuar?")) {
              setValues(initialValues(undefined));
              setMessage(null);
            }
          }}
          className="rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer"
        >
          Restablecer todos a los originales
        </button>
      </div>
    </AdminLayout>
  );
}
